import { Component, OnInit, inject, OnDestroy, ChangeDetectionStrategy, signal, forwardRef } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { Dialog } from '../../interfaces/create-dialog.interface';
import { StepTypeService } from '../../services/step-type/step-type.service';
import { CreateChunk } from '../../interfaces/step/step.interface';
import { StepService } from '../../services/step/step.service';
import { AppStateService } from '../../services/app-state/app-state.service';
import { Notebook, WorkflowUpdate } from '../../interfaces/project.interface';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { CodeEditorComponent } from '../code-editor/code-editor.component';

const MODE_CREATE = 'create';
const MODE_EDIT = 'edit';

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-chunk-dialog',
  templateUrl: './chunk-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatSlideToggleModule,
    // forwardRef breaks an NG0919 circular import: opened by DialogService, and
    // CodeEditorComponent injects DialogService (SCRUM-718, follow-up SCRUM-689).
    forwardRef(() => CodeEditorComponent),
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class ChunkDialogComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private stepTypeService = inject(StepTypeService);
  private stepService = inject(StepService);
  dialogRef = inject<MatDialogRef<ChunkDialogComponent>>(MatDialogRef);
  data = inject<Dialog>(MAT_DIALOG_DATA);

  #appState = inject(AppStateService);
  readonly routineOnlyHelpText =
    'This workflow can only run as a scheduled routine. It cannot be executed in the browser editor.';

  chunkForm: FormGroup;
  chunkTypeList = signal<any[]>([]);
  selectedChunkType = signal<any>(undefined);
  showNotebookInput = false;
  mode: 'create' | 'edit' = MODE_CREATE;
  notebook?: Notebook;

  constructor() {
    const data = this.data;

    this.mode = data.mode || MODE_CREATE;
    this.notebook = data.notebook;
    this.showNotebookInput =
      data.showNotebookInput === undefined ? false : data.showNotebookInput;

    this.chunkForm = this.fb.group({
      name: ['', [Validators.required, this.uniqueNameValidator.bind(this)]],
      steptypeId: ['', Validators.required],
      content: [''],
      description: [''],
      notebookName: [''],
      defaultState: [''],
      headlessOnly: [false],
    });

    if (this.mode === 'edit' && this.notebook) {
      this.chunkForm.patchValue({
        notebookName: this.notebook.name,
        defaultState:
          this.notebook.defaultState ??
          this.#appState.workflow()?.defaultState ??
          '',
        headlessOnly:
          this.notebook.headlessOnly ??
          this.#appState.workflow()?.headlessOnly ??
          false,
      });
      this.chunkForm.get('name')?.disable();
      this.chunkForm.get('steptypeId')?.disable();
      this.chunkForm.get('description')?.disable();
    }
  }

  ngOnInit() {
    if (this.mode === MODE_CREATE) {
      this.stepTypeService.stepTypes$.subscribe((types: any) => {
        this.chunkTypeList.set(types);
      });

      this.chunkForm.get('steptypeId')?.valueChanges.subscribe((value) => {
        this.selectedChunkType.set(this.chunkTypeList().find(
          (type) => type.steptypeId === value,
        ));
      });
    }
  }

  // eslint-disable-next-line
  ngOnDestroy() {
    // intentionally empty
  }

  uniqueNameValidator(control: AbstractControl): ValidationErrors | null {
    const existingStep = this.stepService.stepList.find(
      (step: any) => step.name === control.value,
    );
    return existingStep ? { nonUniqueName: true } : null;
  }

  private buildCreateChunk(): CreateChunk {
    return {
      workflowId: this.#appState.workflowId(),
      name: this.chunkForm.value.name,
      steptypeId: this.chunkForm.value.steptypeId,
      content: this.chunkForm.value.content,
      description: this.chunkForm.value.description,
      notebookName: this.chunkForm.value.notebookName,
    };
  }

  private buildWorkflowUpdate(): WorkflowUpdate {
    return {
      name: this.chunkForm.value.notebookName,
      defaultState: this.chunkForm.value.defaultState,
      headlessOnly: this.chunkForm.value.headlessOnly ?? false,
    };
  }

  onSave() {
    if (!this.chunkForm.valid) {
      return;
    }

    if (this.mode === MODE_CREATE) {
      this.dialogRef.close(this.buildCreateChunk());
    } else if (this.mode === MODE_EDIT) {
      this.dialogRef.close(this.buildWorkflowUpdate());
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  updateContent(description: string) {
    this.chunkForm.get('description')?.setValue(description);
  }

  setFormTouched() {
    this.chunkForm.markAsTouched();
  }
}
